import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { supabase } from "../supabaseClient";

const BRAND = {
  burgundy: "#800020",
  bg: "#0b0b0c",
  card: "rgba(255,255,255,0.04)",
  border: "rgba(255,255,255,0.10)",
  text: "#f6f6f6",
  muted: "rgba(255,255,255,0.70)",
};

const ROLES = [
  "Editor",
  "Writer",
  "Photographer",
  "Designer",
  "Video",
  "Social",
  "Business",
  "Other",
];

const styles = {
  wrap: {
    minHeight: "100vh",
    display: "grid",
    placeItems: "center",
    padding: 18,
    background: `
      radial-gradient(900px 500px at 15% 10%, rgba(128,0,32,0.35), transparent 60%),
      radial-gradient(700px 500px at 85% 20%, rgba(255,255,255,0.05), transparent 55%),
      ${BRAND.bg}
    `,
    color: BRAND.text,
    fontFamily:
      'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"',
  },
  card: {
    width: "100%",
    maxWidth: 440,
    borderRadius: 18,
    border: `1px solid ${BRAND.border}`,
    background: BRAND.card,
    padding: 28,
    boxShadow: "0 20px 60px rgba(0,0,0,0.55)",
    backdropFilter: "blur(8px)",
  },
  label: {
    fontSize: 12,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: BRAND.muted,
    fontWeight: 700,
  },
  input: {
    width: "100%",
    padding: "12px 12px",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(0,0,0,0.35)",
    color: BRAND.text,
    outline: "none",
    fontSize: 14,
    marginTop: 8,
    boxSizing: "border-box",
  },
  select: {
    width: "100%",
    padding: "12px 12px",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(0,0,0,0.55)",
    color: BRAND.text,
    outline: "none",
    fontSize: 14,
    marginTop: 8,
    boxSizing: "border-box",
    appearance: "none",
    cursor: "pointer",
  },
  btn: {
    width: "100%",
    padding: "12px 12px",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.12)",
    background: `linear-gradient(90deg, ${BRAND.burgundy}, rgba(128,0,32,0.65))`,
    color: "#fff",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    marginTop: 14,
  },
  errorBox: {
    marginTop: 12,
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(255,80,80,0.3)",
    background: "rgba(128,0,32,0.20)",
    color: "#fca5a5",
    fontSize: 13,
  },
  successBox: {
    marginTop: 12,
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(0,200,100,0.3)",
    background: "rgba(0,100,50,0.20)",
    color: "#6ee7b7",
    fontSize: 13,
  },
  hint: {
    fontSize: 11,
    color: "rgba(255,255,255,0.35)",
    marginTop: 5,
    lineHeight: 1.5,
  },
};

function strengthLabel(pw) {
  if (!pw) return null;
  const checks = [pw.length >= 8, /[A-Z]/.test(pw), /[0-9]/.test(pw), /[^A-Za-z0-9]/.test(pw)];
  const score = checks.filter(Boolean).length;
  if (score <= 1) return { label: "Weak", color: "#f87171" };
  if (score === 2) return { label: "Fair", color: "#fbbf24" };
  if (score === 3) return { label: "Good", color: "#34d399" };
  return { label: "Strong", color: "#6ee7b7" };
}

export default function Signup({ user }) {
  const navigate = useNavigate();

  const [displayName, setDisplayName] = useState("");
  const [email, setEmail]             = useState("");
  const [role, setRole]               = useState("");
  const [password, setPassword]       = useState("");
  const [confirm, setConfirm]         = useState("");
  const [error, setError]             = useState("");
  const [success, setSuccess]         = useState("");
  const [loading, setLoading]         = useState(false);

  // Already logged in ? redirect
  if (user) return <Navigate to="/dashboard" replace />;

  const strength = strengthLabel(password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const name  = displayName.trim();
    const mail  = email.trim().toLowerCase();

    if (!name || !mail || !role || !password || !confirm) {
      setError("Please fill in all fields.");
      return;
    }

    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    setLoading(true);

    // 1. Create the auth user
    const { data, error: signUpError } = await supabase.auth.signUp({
      email: mail,
      password,
      options: {
        // Store display_name + role in auth metadata as a fallback
        data: { display_name: name, role },
      },
    });

    if (signUpError) {
      setLoading(false);
      setError(signUpError.message);
      return;
    }

    // 2. Insert a matching row in the public profiles table
    //    (the SQL trigger below handles this automatically, but we
    //     also do it here as a belt-and-braces approach)
    if (data.user) {
      await supabase.from("profiles").upsert({
        id:           data.user.id,
        display_name: name,
        role,
        email:        mail,
      });
    }

    setLoading(false);

    // Supabase sends a confirmation email by default.
    // If you've disabled email confirmation in your project settings,
    // the user is signed in immediately � adjust the message below.
    setSuccess(
      "Account created! Check your email to confirm your address, then sign in."
    );
  };

  return (
    <div style={styles.wrap}>
      <div style={styles.card}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
          }}
        >
          Africa Loud
        </div>

        <h1 style={{ margin: "10px 0 6px", fontSize: 22, fontWeight: 900 }}>
          Create account
        </h1>

        <div style={{ fontSize: 13, color: BRAND.muted, lineHeight: 1.5 }}>
          Join the Africa Loud editorial team.
        </div>

        {error   && <div style={styles.errorBox}>{error}</div>}
        {success && <div style={styles.successBox}>{success}</div>}

        {!success && (
          <form onSubmit={handleSubmit} style={{ marginTop: 14 }}>
            {/* Display name */}
            <div>
              <div style={styles.label}>Display name</div>
              <input
                style={styles.input}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="How you'll appear in the dashboard"
                autoComplete="name"
              />
            </div>

            {/* Email */}
            <div style={{ marginTop: 12 }}>
              <div style={styles.label}>Email</div>
              <input
                style={styles.input}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@africaloud.com"
                autoComplete="email"
              />
            </div>

            {/* Role / department */}
            <div style={{ marginTop: 12 }}>
              <div style={styles.label}>Role / Department</div>
              <select
                style={styles.select}
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                <option value="" disabled>
                  Select your role�
                </option>
                {ROLES.map((r) => (
                  <option key={r} value={r.toLowerCase()}>
                    {r}
                  </option>
                ))}
              </select>
            </div>

            {/* Password */}
            <div style={{ marginTop: 12 }}>
              <div
                style={{
                  ...styles.label,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span>Password</span>
                {strength && (
                  <span style={{ color: strength.color, fontSize: 11 }}>
                    {strength.label}
                  </span>
                )}
              </div>
              <input
                style={styles.input}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min. 8 characters"
                type="password"
                autoComplete="new-password"
              />
              <div style={styles.hint}>
                Use uppercase, numbers, and symbols for a stronger password.
              </div>
            </div>

            {/* Confirm password */}
            <div style={{ marginTop: 12 }}>
              <div style={styles.label}>Confirm password</div>
              <input
                style={styles.input}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Re-enter password"
                type="password"
                autoComplete="new-password"
              />
              {confirm && password !== confirm && (
                <div style={{ ...styles.hint, color: "#f87171" }}>
                  Passwords don't match.
                </div>
              )}
            </div>

            <button style={styles.btn} type="submit" disabled={loading}>
              {loading ? "Creating account�" : "Create account"}
            </button>
          </form>
        )}

        <div
          style={{
            marginTop: 16,
            fontSize: 13,
            color: BRAND.muted,
            textAlign: "center",
          }}
        >
          Already have an account?{" "}
          <Link
            to="/login"
            style={{ color: "#f9a8c9", fontWeight: 700, textDecoration: "none" }}
          >
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}